import lodashSet from 'lodash.set';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';

export type TCountryPensionType = TCountryPensionTypeRow[];

export type TCountryPensionTypeRow = {
  grossAmountReceived: Nullable<string>;
  country: Nullable<string>;
  pensionType: Nullable<string>;
  foreignTaxPaid: Nullable<string>;
};

export interface PensionStateValues {
  didYouMakeContributionsToAUsIraRothIraOrSepAccountsDuringTheFilingYear: Nullable<TYesOrNo>;
  form5498ReportingIraorRothIraContributionForTaxYear: Nullable<string>;
  didYouReceiveUsRetirementDistributions: Nullable<string>;
  pleaseUploadForm1099ROrSsa1099: Nullable<string>;
  nonTaxesPaidOnUsRetirementDistributions: Nullable<string>;
  currency: Nullable<string>;
  didYouReceiveForeignieNonUsRetirementDistributions: Nullable<string>;
  countryPensionType: TCountryPensionType;
  didYouPerformARolloverBetweenTraditionalIraAndRothIra: Nullable<TYesOrNo>;
  pleaseUploadSupportingDocumentation: Nullable<string>;
}

export interface PensionState {
  values: PensionStateValues;
}

const initialState: PensionState = {
  values: {
    didYouMakeContributionsToAUsIraRothIraOrSepAccountsDuringTheFilingYear:
      null,
    form5498ReportingIraorRothIraContributionForTaxYear: null,
    didYouReceiveUsRetirementDistributions: null,
    pleaseUploadForm1099ROrSsa1099: null,
    nonTaxesPaidOnUsRetirementDistributions: null,
    currency: null,
    didYouReceiveForeignieNonUsRetirementDistributions: null,
    countryPensionType: [
      {
        grossAmountReceived: null,
        country: null,
        pensionType: null,
        foreignTaxPaid: null,
      },
    ],
    didYouPerformARolloverBetweenTraditionalIraAndRothIra: null,
    pleaseUploadSupportingDocumentation: null,
  },
};

const pensionSlice = createSlice({
  name: 'spouse/pension',
  initialState,
  reducers: {
    update(state, action: PayloadAction<PensionStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof PensionStateValues | keyof TCountryPensionTypeRow;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key: keyof PensionStateValues | keyof TCountryPensionTypeRow;
        value: TCountryPensionType;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field) => ({ ...field }));
      if (action.payload.key === 'countryPensionType') {
        state.values[action.payload.key] = fieldArray;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire.spouseIncome.pension
              .values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } = pensionSlice.actions;
export default pensionSlice.reducer;

export const selectPension = (state: RootState) =>
  state.taxQuestionnaire.spouseIncome.pension;
